import React from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, Lock, ExternalLink } from "lucide-react";
import { useCart } from "../../contexts/CartContext";
import { toast } from "sonner";
import { AggregateRatingBadge } from "../ReviewSection";
import ShareButton from "../ShareButton";
import FulfillmentBadge, { isPurchasable } from "../FulfillmentBadge";

const FILTERS = [
  { id: "merch", label: "Public merch" },
  { id: "workshop_material", label: "Workshop materials" },
  { id: "all", label: "All" },
];

const SOURCE_FILTERS = [
  { id: "any",        label: "All sources" },
  { id: "foundation", label: "Foundation" },
  { id: "vendor",     label: "Vendor partners" },
];

export function ShopFilters({ active, onChange }) {
  return (
    <div className="mt-8 flex gap-2 flex-wrap" data-testid="shop-filters">
      {FILTERS.map((f) => (
        <button
          key={f.id}
          onClick={() => onChange(f.id)}
          className={`px-4 py-2 rounded-full text-xs uppercase tracking-wider font-medium border transition ${
            active === f.id ? "bg-[#476B6B] text-white border-[#476B6B]" : "bg-white text-[#1A2424] border-[#E5E1D8] hover:border-[#476B6B]"
          }`}
          data-testid={`shop-filter-${f.id}`}
        >
          {f.label}
        </button>
      ))}
    </div>
  );
}

/* Source + per-vendor filter row.
 *
 * Lives directly under ShopFilters. Lets shoppers narrow merch by where
 * it comes from: Foundation-stocked, vendor partners overall, or a
 * specific vendor brand. The per-vendor dropdown is only meaningful when
 * "Vendor partners" is selected.
 */
export function ShopSourceFilters({ source, onSourceChange, vendor, onVendorChange, vendors }) {
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="shop-source-filters">
      {SOURCE_FILTERS.map((f) => (
        <button
          key={f.id}
          onClick={() => onSourceChange(f.id)}
          className={`px-3 py-1.5 rounded-full text-[11px] uppercase tracking-wider font-medium border transition ${
            source === f.id ? "bg-[#C9A961] text-white border-[#C9A961]" : "bg-white text-[#1A2424] border-[#E5E1D8] hover:border-[#C9A961]"
          }`}
          data-testid={`shop-source-${f.id}`}
        >
          {f.label}
        </button>
      ))}
      {source === "vendor" && vendors && vendors.length > 0 && (
        <select
          value={vendor || "all"}
          onChange={(e) => onVendorChange(e.target.value)}
          className="text-[11px] uppercase tracking-wider font-medium border border-[#E5E1D8] rounded-full px-3 py-1.5 bg-white"
          data-testid="shop-vendor-select"
        >
          <option value="all">All vendor partners</option>
          {vendors.map((v) => (
            <option key={v.slug || v.name} value={v.slug || v.name}>{v.name}</option>
          ))}
        </select>
      )}
    </div>
  );
}

export function ProductCard({ product }) {
  const { addItem } = useCart();
  const isMaterial = product.type === "workshop_material";
  const isVendor = product.is_vendor_product;
  const isOffSite = product.is_off_site && product.vendor_slug;
  const canBuy = isPurchasable(product);
  const outboundUrl = isOffSite
    ? `${process.env.REACT_APP_BACKEND_URL}/api/out/${product.vendor_slug}?product_id=${product.id}`
    : null;

  return (
    <div className="card card-hover overflow-hidden flex flex-col" data-testid={`product-card-${product.id}`}>
      <Link to={`/equip/${product.id}`} className="block aspect-square bg-[#F4F1EA] overflow-hidden relative">
        <img src={product.image_url} alt={product.image_caption || product.name} className="w-full h-full object-cover" />
        <div className="absolute top-2 left-2">
          <FulfillmentBadge product={product} />
        </div>
      </Link>
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-2">
          <Link to={`/equip/${product.id}`} className="font-serif text-lg leading-tight hover:text-[#476B6B]">
            {product.name}
          </Link>
          {isMaterial && <Lock size={14} strokeWidth={1.5} className="text-[#C9A961] shrink-0 mt-1" />}
          {isOffSite && <ExternalLink size={14} strokeWidth={1.5} className="text-[#476B6B] shrink-0 mt-1" />}
        </div>
        {isVendor && product.vendor_name && (
          <p className="text-[10px] uppercase tracking-wider text-[#C9A961] mt-1" data-testid={`product-vendor-badge-${product.id}`}>
            By{" "}
            {product.vendor_slug ? (
              <Link to={`/partner/${product.vendor_slug}`} className="text-[#476B6B] hover:underline" onClick={(e) => e.stopPropagation()}>
                {product.vendor_name}
              </Link>
            ) : (
              <span className="text-[#476B6B]">{product.vendor_name}</span>
            )}
          </p>
        )}
        <p className="text-xs text-[#5C6B6B] mt-1 line-clamp-2 flex-1">{product.description}</p>
        <div className="mt-2">
          <AggregateRatingBadge subjectType="product" subjectId={product.id} />
        </div>
        <div className="mt-4 flex items-center justify-between gap-2">
          <span className="font-medium text-[#1A2424]">${product.price?.toFixed(2)}</span>
          <div className="flex items-center gap-1.5">
            {!isMaterial && (
              <ShareButton
                surface="product"
                surfaceId={product.id}
                path={`/equip/${product.id}`}
                title={product.name}
                emailSubject={`From the birthright shop: ${product.name}`}
                size="sm"
              />
            )}
            {isMaterial ? (
              <Link to={`/equip/${product.id}`} className="text-xs text-[#476B6B] font-medium hover:underline">
                Participants only
              </Link>
            ) : isOffSite ? (
              <a
                href={outboundUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="text-xs flex items-center gap-1.5 bg-[#476B6B] text-white px-3 py-1.5 rounded-full hover:bg-[#3A5858] transition"
                data-testid={`product-buy-external-${product.id}`}
                onClick={(e) => e.stopPropagation()}
              >
                <ExternalLink size={12} strokeWidth={1.5} /> Buy on {product.vendor_name?.split(" ")[0] || "vendor"}
              </a>
            ) : !canBuy ? (
              <Link
                to={`/equip/${product.id}`}
                className="text-xs text-[#7C5316] font-medium hover:underline"
                data-testid={`product-sample-link-${product.id}`}
                title="Concept image — no fulfillment path yet"
              >
                Preview only
              </Link>
            ) : (
              <button
                onClick={() => {
                  addItem(product);
                  toast.success(`Added ${product.name}`);
                }}
                className="text-xs flex items-center gap-1.5 bg-[#476B6B] text-white px-3 py-1.5 rounded-full hover:bg-[#3A5858] transition"
                data-testid={`product-add-${product.id}`}
              >
                <ShoppingBag size={12} strokeWidth={1.5} /> Add
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function ProductGrid({ products }) {
  return (
    <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="shop-products">
      {products.map((p) => <ProductCard key={p.id} product={p} />)}
    </div>
  );
}
